/** A generated canvas layout as serializable Fabric.js-compatible object descriptors. */
export interface GeneratedLayout {
  objects: FabricObjectDescriptor[];
}

export interface FabricObjectDescriptor {
  type: "rect" | "textbox" | "ellipse" | "line" | "path";
  left: number;
  top: number;
  width?: number;
  height?: number;
  rx?: number;
  ry?: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  angle?: number;
  scaleX?: number;
  scaleY?: number;
  text?: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string | number;
  textAlign?: string;
  customId?: string;
  customName?: string;
  selectable?: boolean;
}

export interface BrandKit {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  fontFamily: string;
  headingFontFamily: string;
}

export const DEFAULT_BRAND_KIT: BrandKit = {
  primaryColor: "#6366f1",
  secondaryColor: "#8b5cf6",
  accentColor: "#f59e0b",
  backgroundColor: "#ffffff",
  textColor: "#1f2937",
  fontFamily: "Inter, sans-serif",
  headingFontFamily: "Inter, sans-serif",
};

type LayoutStyle = "bold" | "minimal" | "gradient" | "split" | "centered";

const STYLE_PALETTES: Record<LayoutStyle, { bg: string; accent: string; text: string }> = {
  bold: { bg: "#1f2937", accent: "#f59e0b", text: "#ffffff" },
  minimal: { bg: "#ffffff", accent: "#6366f1", text: "#1f2937" },
  gradient: { bg: "#4f46e5", accent: "#818cf8", text: "#ffffff" },
  split: { bg: "#f8fafc", accent: "#0ea5e9", text: "#0f172a" },
  centered: { bg: "#fef3c7", accent: "#d97706", text: "#78350f" },
};

const LAYOUT_STYLES: LayoutStyle[] = ["bold", "minimal", "gradient", "split", "centered"];

function pickStyle(prompt: string): LayoutStyle {
  const lower = prompt.toLowerCase();
  if (lower.includes("bold") || lower.includes("sale") || lower.includes("announcement"))
    return "bold";
  if (lower.includes("minimal") || lower.includes("clean") || lower.includes("simple"))
    return "minimal";
  if (lower.includes("gradient") || lower.includes("modern") || lower.includes("tech"))
    return "gradient";
  if (lower.includes("split") || lower.includes("compare") || lower.includes("versus"))
    return "split";
  if (lower.includes("warm") || lower.includes("friendly") || lower.includes("welcome"))
    return "centered";
  return LAYOUT_STYLES[Math.floor(Math.random() * LAYOUT_STYLES.length)] ?? "bold";
}

function extractTextFromPrompt(prompt: string): { heading: string; subtext: string } {
  const cleaned = prompt
    .replace(/^(create|make|design|generate)\s+(a|an|me)?\s*/i, "")
    .replace(
      /\s*(instagram|facebook|twitter|linkedin|youtube|social media|post|story|banner|cover|thumbnail)\s*/gi,
      "",
    )
    .replace(/\s*(about|for|with|featuring)\s*/i, "|")
    .trim();

  const parts = cleaned
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);

  const first = parts[0] ?? "";

  if (parts.length >= 2) {
    return {
      heading: capitalize(first),
      subtext: capitalize(parts.slice(1).join(" ")),
    };
  }

  if (first.length > 30) {
    const words = first.split(" ");
    const mid = Math.ceil(words.length / 2);
    return {
      heading: capitalize(words.slice(0, mid).join(" ")),
      subtext: capitalize(words.slice(mid).join(" ")),
    };
  }

  return {
    heading: first ? capitalize(first) : "Your Title Here",
    subtext: "Add your message",
  };
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Generate a canvas layout from a text prompt and artboard dimensions. */
export function generateLayout(
  prompt: string,
  artboardWidth: number,
  artboardHeight: number,
  brandKit: BrandKit = DEFAULT_BRAND_KIT,
): GeneratedLayout {
  const style = pickStyle(prompt);
  const palette = STYLE_PALETTES[style];
  const { heading, subtext } = extractTextFromPrompt(prompt);
  const isLandscape = artboardWidth > artboardHeight;
  const _isSquare = Math.abs(artboardWidth - artboardHeight) < 100;

  const objects: FabricObjectDescriptor[] = [];

  // Background
  objects.push({
    type: "rect",
    left: 0,
    top: 0,
    width: artboardWidth,
    height: artboardHeight,
    fill: palette.bg,
    selectable: false,
    customId: "__bg__",
    customName: "Background",
  });

  switch (style) {
    case "bold":
      objects.push(
        ...buildBoldLayout(artboardWidth, artboardHeight, palette, heading, subtext, brandKit),
      );
      break;
    case "minimal":
      objects.push(
        ...buildMinimalLayout(artboardWidth, artboardHeight, palette, heading, subtext, brandKit),
      );
      break;
    case "gradient":
      objects.push(
        ...buildGradientLayout(artboardWidth, artboardHeight, palette, heading, subtext, brandKit),
      );
      break;
    case "split":
      objects.push(
        ...buildSplitLayout(
          artboardWidth,
          artboardHeight,
          palette,
          heading,
          subtext,
          isLandscape,
          brandKit,
        ),
      );
      break;
    case "centered":
      objects.push(
        ...buildCenteredLayout(artboardWidth, artboardHeight, palette, heading, subtext, brandKit),
      );
      break;
  }

  return { objects };
}

function buildBoldLayout(
  w: number,
  h: number,
  palette: (typeof STYLE_PALETTES)["bold"],
  heading: string,
  subtext: string,
  brand: BrandKit,
): FabricObjectDescriptor[] {
  const pad = Math.min(w, h) * 0.08;
  const headingSize = Math.min(w, h) * 0.1;
  const subtextSize = headingSize * 0.4;

  return [
    // Accent bar at top
    {
      type: "rect",
      left: 0,
      top: 0,
      width: w,
      height: h * 0.06,
      fill: palette.accent,
      customName: "Accent Bar",
    },
    // Heading
    {
      type: "textbox",
      left: pad,
      top: h * 0.3,
      width: w - pad * 2,
      text: heading.toUpperCase(),
      fontSize: headingSize,
      fontFamily: brand.headingFontFamily,
      fontWeight: "900",
      fill: palette.text,
      textAlign: "left",
      customName: "Heading",
    },
    // Subtext
    {
      type: "textbox",
      left: pad,
      top: h * 0.55,
      width: w - pad * 2,
      text: subtext,
      fontSize: subtextSize,
      fontFamily: brand.fontFamily,
      fontWeight: "400",
      fill: palette.accent,
      textAlign: "left",
      customName: "Subtext",
    },
    // Bottom accent line
    {
      type: "rect",
      left: pad,
      top: h * 0.85,
      width: w * 0.3,
      height: 4,
      fill: palette.accent,
      customName: "Accent Line",
    },
  ];
}

function buildMinimalLayout(
  w: number,
  h: number,
  palette: (typeof STYLE_PALETTES)["minimal"],
  heading: string,
  subtext: string,
  brand: BrandKit,
): FabricObjectDescriptor[] {
  const headingSize = Math.min(w, h) * 0.08;
  const subtextSize = headingSize * 0.45;

  return [
    // Border frame
    {
      type: "rect",
      left: w * 0.04,
      top: h * 0.04,
      width: w * 0.92,
      height: h * 0.92,
      fill: "transparent",
      stroke: palette.accent,
      strokeWidth: 2,
      customName: "Frame",
    },
    // Centered heading
    {
      type: "textbox",
      left: w * 0.1,
      top: h * 0.35,
      width: w * 0.8,
      text: heading,
      fontSize: headingSize,
      fontFamily: brand.headingFontFamily,
      fontWeight: "600",
      fill: palette.text,
      textAlign: "center",
      customName: "Heading",
    },
    // Divider
    {
      type: "rect",
      left: w * 0.4,
      top: h * 0.52,
      width: w * 0.2,
      height: 2,
      fill: palette.accent,
      customName: "Divider",
    },
    // Subtext
    {
      type: "textbox",
      left: w * 0.15,
      top: h * 0.58,
      width: w * 0.7,
      text: subtext,
      fontSize: subtextSize,
      fontFamily: brand.fontFamily,
      fontWeight: "400",
      fill: "#6b7280",
      textAlign: "center",
      customName: "Subtext",
    },
  ];
}

function buildGradientLayout(
  w: number,
  h: number,
  palette: (typeof STYLE_PALETTES)["gradient"],
  heading: string,
  subtext: string,
  brand: BrandKit,
): FabricObjectDescriptor[] {
  const headingSize = Math.min(w, h) * 0.09;
  const subtextSize = headingSize * 0.4;

  return [
    // Overlay gradient band
    {
      type: "rect",
      left: 0,
      top: h * 0.6,
      width: w,
      height: h * 0.4,
      fill: "rgba(0,0,0,0.3)",
      customName: "Overlay",
    },
    // Decorative circle
    {
      type: "ellipse",
      left: w * 0.7,
      top: -h * 0.15,
      rx: w * 0.25,
      ry: w * 0.25,
      fill: palette.accent,
      opacity: 0.3,
      customName: "Deco Circle",
    },
    // Heading
    {
      type: "textbox",
      left: w * 0.08,
      top: h * 0.35,
      width: w * 0.84,
      text: heading,
      fontSize: headingSize,
      fontFamily: brand.headingFontFamily,
      fontWeight: "700",
      fill: palette.text,
      textAlign: "center",
      customName: "Heading",
    },
    // Subtext
    {
      type: "textbox",
      left: w * 0.12,
      top: h * 0.65,
      width: w * 0.76,
      text: subtext,
      fontSize: subtextSize,
      fontFamily: brand.fontFamily,
      fontWeight: "400",
      fill: "rgba(255,255,255,0.85)",
      textAlign: "center",
      customName: "Subtext",
    },
  ];
}

function buildSplitLayout(
  w: number,
  h: number,
  palette: (typeof STYLE_PALETTES)["split"],
  heading: string,
  subtext: string,
  isLandscape: boolean,
  brand: BrandKit,
): FabricObjectDescriptor[] {
  const headingSize = Math.min(w, h) * 0.08;
  const subtextSize = headingSize * 0.45;

  if (isLandscape) {
    return [
      // Left colored panel
      {
        type: "rect",
        left: 0,
        top: 0,
        width: w * 0.45,
        height: h,
        fill: palette.accent,
        customName: "Color Panel",
      },
      // Right content
      {
        type: "textbox",
        left: w * 0.52,
        top: h * 0.3,
        width: w * 0.42,
        text: heading,
        fontSize: headingSize,
        fontFamily: brand.headingFontFamily,
        fontWeight: "700",
        fill: palette.text,
        textAlign: "left",
        customName: "Heading",
      },
      {
        type: "textbox",
        left: w * 0.52,
        top: h * 0.55,
        width: w * 0.42,
        text: subtext,
        fontSize: subtextSize,
        fontFamily: brand.fontFamily,
        fontWeight: "400",
        fill: "#64748b",
        textAlign: "left",
        customName: "Subtext",
      },
    ];
  }

  return [
    // Top colored panel
    {
      type: "rect",
      left: 0,
      top: 0,
      width: w,
      height: h * 0.45,
      fill: palette.accent,
      customName: "Color Panel",
    },
    // Bottom content
    {
      type: "textbox",
      left: w * 0.08,
      top: h * 0.52,
      width: w * 0.84,
      text: heading,
      fontSize: headingSize,
      fontFamily: brand.headingFontFamily,
      fontWeight: "700",
      fill: palette.text,
      textAlign: "center",
      customName: "Heading",
    },
    {
      type: "textbox",
      left: w * 0.12,
      top: h * 0.72,
      width: w * 0.76,
      text: subtext,
      fontSize: subtextSize,
      fontFamily: brand.fontFamily,
      fontWeight: "400",
      fill: "#64748b",
      textAlign: "center",
      customName: "Subtext",
    },
  ];
}

function buildCenteredLayout(
  w: number,
  h: number,
  palette: (typeof STYLE_PALETTES)["centered"],
  heading: string,
  subtext: string,
  brand: BrandKit,
): FabricObjectDescriptor[] {
  const headingSize = Math.min(w, h) * 0.09;
  const subtextSize = headingSize * 0.4;

  return [
    // Decorative rounded rect
    {
      type: "rect",
      left: w * 0.1,
      top: h * 0.15,
      width: w * 0.8,
      height: h * 0.7,
      fill: "rgba(255,255,255,0.7)",
      rx: 20,
      ry: 20,
      customName: "Card",
    },
    // Heading
    {
      type: "textbox",
      left: w * 0.15,
      top: h * 0.3,
      width: w * 0.7,
      text: heading,
      fontSize: headingSize,
      fontFamily: brand.headingFontFamily,
      fontWeight: "700",
      fill: palette.text,
      textAlign: "center",
      customName: "Heading",
    },
    // Subtext
    {
      type: "textbox",
      left: w * 0.18,
      top: h * 0.55,
      width: w * 0.64,
      text: subtext,
      fontSize: subtextSize,
      fontFamily: brand.fontFamily,
      fontWeight: "400",
      fill: palette.text,
      textAlign: "center",
      opacity: 0.7,
      customName: "Subtext",
    },
    // CTA button shape
    {
      type: "rect",
      left: w * 0.3,
      top: h * 0.72,
      width: w * 0.4,
      height: Math.min(w, h) * 0.07,
      fill: palette.accent,
      rx: 8,
      ry: 8,
      customName: "CTA Button",
    },
    {
      type: "textbox",
      left: w * 0.3,
      top: h * 0.725,
      width: w * 0.4,
      text: "Learn More",
      fontSize: subtextSize * 0.9,
      fontFamily: brand.fontFamily,
      fontWeight: "600",
      fill: "#ffffff",
      textAlign: "center",
      customName: "CTA Text",
    },
  ];
}

/** Pre-built template definitions for the template browser. */
export interface CanvasTemplate {
  id: string;
  name: string;
  category: "social" | "business" | "marketing" | "presentation";
  presetId: string;
  style: LayoutStyle;
  description: string;
  previewPrompt: string;
}

export const CANVAS_TEMPLATES: CanvasTemplate[] = [
  {
    id: "ig-post-bold",
    name: "Bold Announcement",
    category: "social",
    presetId: "instagram-post",
    style: "bold",
    description: "High-impact Instagram post with bold typography",
    previewPrompt: "New Product Launch",
  },
  {
    id: "ig-post-minimal",
    name: "Clean & Minimal",
    category: "social",
    presetId: "instagram-post",
    style: "minimal",
    description: "Minimalist Instagram post with elegant framing",
    previewPrompt: "Mindful Living",
  },
  {
    id: "ig-story-gradient",
    name: "Gradient Story",
    category: "social",
    presetId: "instagram-story",
    style: "gradient",
    description: "Modern gradient Instagram story",
    previewPrompt: "Summer Vibes",
  },
  {
    id: "fb-cover-split",
    name: "Split Cover",
    category: "social",
    presetId: "facebook-cover",
    style: "split",
    description: "Two-tone Facebook cover photo",
    previewPrompt: "Welcome to Our Community",
  },
  {
    id: "linkedin-minimal",
    name: "Professional Banner",
    category: "business",
    presetId: "linkedin-banner",
    style: "minimal",
    description: "Clean LinkedIn banner for professionals",
    previewPrompt: "Innovation & Strategy",
  },
  {
    id: "twitter-bold",
    name: "Tweet Graphic",
    category: "social",
    presetId: "twitter-post",
    style: "bold",
    description: "Eye-catching Twitter/X post graphic",
    previewPrompt: "Breaking News",
  },
  {
    id: "yt-gradient",
    name: "YouTube Thumbnail",
    category: "social",
    presetId: "youtube-thumbnail",
    style: "gradient",
    description: "Vibrant YouTube thumbnail",
    previewPrompt: "Top 10 Tips",
  },
  {
    id: "presentation-centered",
    name: "Title Slide",
    category: "presentation",
    presetId: "presentation",
    style: "centered",
    description: "Warm presentation title slide",
    previewPrompt: "Quarterly Review",
  },
  {
    id: "bcard-minimal",
    name: "Business Card",
    category: "business",
    presetId: "business-card",
    style: "minimal",
    description: "Elegant business card layout",
    previewPrompt: "Jane Smith | Designer",
  },
  {
    id: "invoice-split",
    name: "Invoice Header",
    category: "business",
    presetId: "invoice",
    style: "split",
    description: "Professional invoice header layout",
    previewPrompt: "Invoice #1234",
  },
  {
    id: "sale-bold",
    name: "Flash Sale",
    category: "marketing",
    presetId: "instagram-post",
    style: "bold",
    description: "High-energy sale promotion post",
    previewPrompt: "50% Off Everything",
  },
  {
    id: "launch-gradient",
    name: "Product Launch",
    category: "marketing",
    presetId: "instagram-post",
    style: "gradient",
    description: "Modern product launch announcement",
    previewPrompt: "Introducing Our New Product",
  },
];

export function getTemplatesByCategory(): Map<string, CanvasTemplate[]> {
  const map = new Map<string, CanvasTemplate[]>();
  for (const template of CANVAS_TEMPLATES) {
    const list = map.get(template.category) ?? [];
    list.push(template);
    map.set(template.category, list);
  }
  return map;
}
